import { motion } from 'framer-motion';
import Footer from '../components/Footer';

const ValueAdditionPage = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 }
    }
  };

  return (
    <div>
      <section className="pt-32 pb-20 bg-gradient-to-br from-tev-green to-tev-green-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">VALUE ADDITION</h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto opacity-90">
              Transforming raw agricultural products into high-quality, market-ready goods
            </p>
          </motion.div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={itemVariants} className="relative order-2 md:order-1">
              <img 
                src="/images/process.jpg" 
                alt="Value Addition Processing" 
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent rounded-2xl"></div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="order-1 md:order-2 bg-gradient-to-br from-tev-green-light to-tev-green-bright p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="bg-tev-green text-white p-4 rounded-full mr-4">
                  <i className="fas fa-cube text-2xl"></i>
                </div>
                <h3 className="text-3xl font-bold text-white">OUR VALUE ADDITION DIVISION</h3>
              </div>
              <p className="text-lg text-white leading-relaxed mb-6">
                Our value addition activities are focused on transforming raw agricultural products into high-quality, market-ready goods that result in 
                increase profitability for our farmer-partners and strengthen local agribusiness. By improving processing, packaging, and product 
                quality, we help unlock the full economic potential of each crop and create new opportunities across the value chain.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-xl shadow-lg border border-tev-green">
                  <div className="flex items-center mb-3">
                    <i className="fas fa-map-marker-alt text-tev-green text-xl mr-3"></i>
                    <h4 className="font-semibold text-gray-900">Lilongwe Facility</h4>
                  </div>
                  <p className="text-gray-700">End-to-end processing of export-quality peanuts and common beans</p>
                </div>
                <div className="bg-white p-4 rounded-xl shadow-lg border border-tev-green-light">
                  <div className="flex items-center mb-3">
                    <i className="fas fa-chart-line text-tev-green-light text-xl mr-3"></i>
                    <h4 className="font-semibold text-gray-900">Market Ready</h4>
                  </div>
                  <p className="text-gray-700">High-quality goods for regional and international markets</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Processing Activities */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Processing Activities</h2>
          </motion.div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-2 gap-8"
          >
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="bg-amber-600 text-white p-4 rounded-full mr-4">
                  <i className="fas fa-peanut-hot text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Peanut Processing</h3>
              </div>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-xl shadow-lg border border-amber-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Processing Steps</h4>
                  <ul className="space-y-2 text-gray-700">
                    <div className="flex items-center mb-3">
                      <i className="fas fa-check-circle text-amber-500 mr-2 mt-1"></i>
                      <span>Shelling, cleaning, sizing and grading</span>
                    </div>
                    <li className="flex items-start">
                      <i className="fas fa-check-circle text-amber-500 mr-2 mt-1"></i>
                      <span>Roasted and blanched nuts production</span>
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-check-circle text-amber-500 mr-2 mt-1"></i>
                      <span>Crude peanut oil and cake production</span>
                    </li>
                    <li className="flex items-start">
                      <i className="fas fa-check-circle text-amber-500 mr-2 mt-1"></i>
                      <span>Peanut butter and paste for RUTFs</span>
                    </li>
                  </ul>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="bg-tev-green text-white p-4 rounded-full mr-4">
                  <i className="fas fa-seedling text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Common Beans Processing</h3>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-lg border border-green-200">
                <h4 className="font-semibold text-gray-900 mb-2">Processing Steps</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="fas fa-check-circle text-green-500 mr-2 mt-1"></i>
                    <span>Cleaning, grading and packing</span>
                  </li>
                  <li className="flex items-start">
                    <i className="fas fa-check-circle text-green-500 mr-2 mt-1"></i>
                    <span>Red speckled, NUA 45 and white haricot varieties</span>
                  </li>
                  <li className="flex items-start">
                    <i className="fas fa-check-circle text-green-500 mr-2 mt-1"></i>
                    <span>Export to regional packing and canning companies</span>
                  </li>
                  <li className="flex items-start">
                    <i className="fas fa-check-circle text-green-500 mr-2 mt-1"></i>
                    <span>Domestic market supply</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* TEV AFRICA FOODS - Technology Section */}
      <section className="py-16 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center mb-12"
          >
            <div className="inline-flex items-center bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-4 rounded-full shadow-xl mb-8">
              <i className="fas fa-building text-white text-2xl mr-3"></i>
              <h2 className="text-white font-bold text-2xl">TEV AFRICA FOODS</h2>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">OUR TECHNOLOGIES AND CAPACITY</h2>
          </motion.div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-2 gap-8"
          >
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="bg-blue-600 text-white p-4 rounded-full mr-4">
                  <i className="fas fa-sort text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Grading Technology</h3>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-blue-500">
                <div className="flex items-center mb-4">
                  <i className="fas fa-microchip text-blue-500 text-xl mr-3"></i>
                  <h4 className="font-bold text-gray-900">Buhler Sortex 2-Shute</h4>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Colour sorter with hand grading belts for precise quality control
                </p>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600 mb-2">150 MT/week</p>
                  <p className="text-gray-600">Single shift capacity</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="bg-amber-600 text-white p-4 rounded-full mr-4">
                  <i className="fas fa-fire text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Roasting Facility</h3>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-amber-500">
                <div className="flex items-center mb-4">
                  <i className="fas fa-temperature-high text-amber-500 text-xl mr-3"></i>
                  <h4 className="font-bold text-gray-900">Nut Processing</h4>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Roasting and blanching various nuts with single shift operation
                </p>
                <div className="bg-amber-50 p-4 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600 mb-2">10 MT/week</p>
                  <p className="text-gray-600">Single shift capacity</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="bg-green-600 text-white p-4 rounded-full mr-4">
                  <i className="fas fa-blender text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Pasting Line</h3>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
                <div className="flex items-center mb-4">
                  <i className="fas fa-paste text-green-500 text-xl mr-3"></i>
                  <h4 className="font-bold text-gray-900">Peanut Butter Production</h4>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Local market production with export potential to neighbouring countries
                </p>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-2xl font-bold text-green-600 mb-2">10 MT/week</p>
                  <p className="text-gray-600">Using 2 pasting machines</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-2xl">
              <div className="flex items-center mb-6">
                <div className="bg-purple-600 text-white p-4 rounded-full mr-4">
                  <i className="fas fa-oil-can text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Oil Extraction</h3>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-purple-500">
                <div className="flex items-center mb-4">
                  <i className="fas fa-industry text-purple-500 text-xl mr-3"></i>
                  <h4 className="font-bold text-gray-900">Cold Press Technology</h4>
                </div>
                <p className="text-gray-700 leading-relaxed mb-4">
                  German-origin cold press for high-quality peanut oil production
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-purple-50 p-3 rounded-lg text-center">
                    <i className="fas fa-tint text-purple-500 text-xl mb-2"></i>
                    <p className="text-sm font-semibold text-purple-700">Peanut Oil</p>
                  </div>
                  <div className="bg-pink-50 p-3 rounded-lg text-center">
                    <i className="fas fa-cube text-pink-500 text-xl mb-2"></i>
                    <p className="text-sm font-semibold text-pink-700">Peanut Cake</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Quality Assurance Section */}
      <section className="py-16 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-8">QUALITY ASSURANCE</h2>
          </motion.div>
          
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid md:grid-cols-2 gap-8"
          >
            <motion.div variants={itemVariants} className="bg-white p-8 rounded-2xl shadow-xl">
              <div className="flex items-center mb-6">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-full mr-4">
                  <i className="fas fa-shield-alt text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Quality First Approach</h3>
              </div>
              <div className="space-y-4">
                <p className="text-gray-700 leading-relaxed mb-6">
                  Quality is at the centre of our production activities. We focus on quality in order to consistently satisfy the needs of our 
                  customers and therefore to build a solid customer base.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <i className="fas fa-check-circle text-indigo-500 mr-3 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900">Regional Standards</h4>
                      <p className="text-gray-600">Meeting QA standards set by different countries in the region</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="fas fa-check-circle text-purple-500 mr-3 mt-1"></i>
                    <div>
                      <h4 className="font-semibold text-gray-900">Extensive Testing</h4>
                      <p className="text-gray-600">Hundreds of aflatoxin tests performed monthly</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div variants={itemVariants} className="bg-white p-8 rounded-2xl shadow-xl">
              <div className="flex items-center mb-6">
                <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white p-4 rounded-full mr-4">
                  <i className="fas fa-microscope text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Testing & Analysis</h3>
              </div>
              <div className="space-y-4">
                <div className="bg-amber-50 p-6 rounded-xl border border-amber-200">
                  <div className="flex items-center mb-4">
                    <i className="fas fa-flask text-amber-500 text-xl mr-3"></i>
                    <h4 className="font-bold text-gray-900">Internal Laboratory</h4>
                  </div>
                  <p className="text-gray-700">Testing buying quality and graded stocks for export markets</p>
                </div>
                <div className="bg-orange-50 p-6 rounded-xl border border-orange-200">
                  <div className="flex items-center mb-4">
                    <i className="fas fa-users text-orange-500 text-xl mr-3"></i>
                    <h4 className="font-bold text-gray-900">Expert Team</h4>
                  </div>
                  <p className="text-gray-700">Mix of new expertise and veterans with customer understanding</p>
                </div>
              </div>
              <div className="mt-6 p-6 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-xl">
                <p className="text-center text-gray-700 italic">
                  "Our mission is ensuring that the right quality of nut is matched with the right customer."
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ValueAdditionPage;
